import {
  FC, useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'next-i18next';
import Label from 'src/common/components/Label';
import { Field, useForm, type FieldInputProps } from 'react-final-form';
import { DEPARTURE_TIME_FIELD, type BookNowDirectFormState } from 'src/book-now-direct/constants';
import { timeStringToDateUpdate } from 'src/common/helpers';
import Dropdown, { type DropdownOption } from 'src/common/components/DropdownDS22';
import { useBookNowDirectContext } from 'src/book-now-direct/providers/BookNowDirectProvider';
import Typography from 'src/common/components/Typography';
import { formatDate } from 'src/common/utils/dateHelpers';
import { BOOK_NOW_DEPARTURE_TIME_FORMAT } from 'src/common/constants';
import { reduceTripTimes } from 'src/book-now-direct/helpers';
import styles from './DepartureTimeField.module.scss';

/**
 * It also tracks previous selection to show warning text if previously selected time
 * is no longer available after changing duration.
 */
const DepartureTimeField: FC = () => {
  const [t] = useTranslation('book-now');
  const [tDirect] = useTranslation('book-now-direct');
  const { change, getState } = useForm<BookNowDirectFormState>();
  const { values: { duration, departTime } } = getState();
  const { allInstabooksOnDate } = useBookNowDirectContext();

  const pickupTimesForDuration = useMemo(() => (
    reduceTripTimes(
      duration
        ? allInstabooksOnDate?.filter(({ trip_length }) => trip_length === duration)
          .flatMap(({ instabook_trip_times }) => instabook_trip_times)
        : allInstabooksOnDate?.flatMap(({ instabook_trip_times }) => instabook_trip_times),
    )
  ), [allInstabooksOnDate, duration]);

  const currentTimeOptionsForDuration = useMemo(
    () => pickupTimesForDuration.map((pickupTimeString) => {
      const date = timeStringToDateUpdate(pickupTimeString);
      const formattedTime = formatDate(date, BOOK_NOW_DEPARTURE_TIME_FORMAT);
      return {
        id: pickupTimeString,
        selectedLabel: formattedTime,
        dropdownLabel: formattedTime,
      };
    }),
    [pickupTimesForDuration],
  );

  const onChange = useCallback((
    input: FieldInputProps<DropdownOption, HTMLElement>,
  ) => (option: DropdownOption) => {
    input.onChange(option.id);
  }, []);

  const [prevDepartTime, setPrevDepartTime] = useState(departTime);

  useEffect(() => {
    if (departTime) {
      setPrevDepartTime(departTime);
    }
  }, [allInstabooksOnDate, departTime]);

  const prevInNew = currentTimeOptionsForDuration.find(({ id }) => id === prevDepartTime);
  const warning = Boolean((prevDepartTime && !prevInNew));

  /** When warning exists, return null to show empty "--:--", encouraging user to fix */
  const selectedOption = warning
    ? null : currentTimeOptionsForDuration?.find(({ id }) => departTime === id);

  useEffect(() => {
    // if prev in new, select it
    if (!selectedOption && prevInNew) {
      change(DEPARTURE_TIME_FIELD, prevInNew.id);
    }
  }, [change, currentTimeOptionsForDuration, prevDepartTime, prevInNew, selectedOption]);

  useEffect(() => {
    // if warning, unset time so captainOption can recalc for duration without time
    if (warning) { change(DEPARTURE_TIME_FIELD, undefined); }
  }, [change, warning]);

  return (
    <div className={styles.root}>
      <Label
        htmlFor={DEPARTURE_TIME_FIELD}
        label={t('book-now--form-departure-time-label')}
        size="m"
      />
      <Field name={DEPARTURE_TIME_FIELD}>
        {({ input, meta }) => (
          <Dropdown
            instructionLabel="--:--"
            options={currentTimeOptionsForDuration}
            onChange={onChange(input)}
            // null (not undefined) keeps downshift "controlled" and avoids console error
            selectedValue={selectedOption || null}
            variant="white"
            buttonSize="l"
            optionSize="m"
            limitMenuHeight
            selectedIconId="clock"
            selectedIconSize="l"
            error={(meta.submitFailed && meta.error) || warning}
          />
        )}
      </Field>
      {warning && prevDepartTime && (
        <div className={styles.warning}>
          <Typography
            size="body-s"
            type="span"
          >
            {tDirect('not-available-time', { time: formatDate(timeStringToDateUpdate(prevDepartTime), BOOK_NOW_DEPARTURE_TIME_FORMAT) })}
          </Typography>
        </div>
      )}
    </div>
  );
};

export default DepartureTimeField;
